import { AppState, Constraint, InvalidStateError } from "./types.ts";

/**
 * Creates a default empty state
 */
export function createDefaultState(): AppState {
  return {
    names: [],
    constraints: [],
    seed: Date.now(),
    metadataFields: [],
    metadata: [],
  };
}

/**
 * Creates initial state with given names
 */
export function createInitialState(names: string[]): AppState {
  const n = names.length;
  const constraints: Constraint[][] = Array.from({ length: n }, () =>
    Array.from({ length: n }, (_, j) => (j === 0 ? Constraint.CAN : Constraint.CAN))
  );

  // Set diagonal to CANNOT (can't give to yourself)
  for (let i = 0; i < n; i++) {
    constraints[i][i] = Constraint.CANNOT;
  }

  return {
    names,
    constraints,
    seed: Date.now(),
    metadataFields: [],
    metadata: Array.from({ length: n }, () => []),
  };
}

/**
 * Validates state structure
 */
export function validateState(state: AppState): void {
  if (!Array.isArray(state.names)) {
    throw new InvalidStateError("names must be an array");
  }

  if (!Array.isArray(state.constraints)) {
    throw new InvalidStateError("constraints must be an array");
  }

  const n = state.names.length;

  if (state.constraints.length !== n) {
    throw new InvalidStateError(
      `constraints matrix must have ${n} rows, got ${state.constraints.length}`,
    );
  }

  for (let i = 0; i < n; i++) {
    if (!Array.isArray(state.constraints[i])) {
      throw new InvalidStateError(`constraints[${i}] must be an array`);
    }
    if (state.constraints[i].length !== n) {
      throw new InvalidStateError(
        `constraints[${i}] must have ${n} columns, got ${state.constraints[i].length}`,
      );
    }
    for (let j = 0; j < n; j++) {
      const val = state.constraints[i][j];
      if (![Constraint.CAN, Constraint.MUST, Constraint.CANNOT].includes(val)) {
        throw new InvalidStateError(
          `constraints[${i}][${j}] has invalid value ${val}`,
        );
      }
    }
  }

  if (typeof state.seed !== "number" || !Number.isFinite(state.seed)) {
    throw new InvalidStateError("seed must be a finite number");
  }

  if (!Array.isArray(state.metadataFields)) {
    throw new InvalidStateError("metadataFields must be an array");
  }

  if (!Array.isArray(state.metadata)) {
    throw new InvalidStateError("metadata must be an array");
  }

  if (state.metadata.length !== n) {
    throw new InvalidStateError(
      `metadata must have ${n} rows, got ${state.metadata.length}`,
    );
  }

  const m = state.metadataFields.length;
  for (let i = 0; i < n; i++) {
    if (!Array.isArray(state.metadata[i])) {
      throw new InvalidStateError(`metadata[${i}] must be an array`);
    }
    if (state.metadata[i].length !== m) {
      throw new InvalidStateError(
        `metadata[${i}] must have ${m} columns, got ${state.metadata[i].length}`,
      );
    }
  }
}

/**
 * Serializes state to URL hash string
 * Uses UTF-8 encoding to support Unicode characters (emojis, non-Latin scripts, etc.)
 */
export function serializeState(state: AppState): string {
  validateState(state);

  const json = JSON.stringify({
    n: state.names,
    c: state.constraints,
    s: state.seed,
    mf: state.metadataFields,
    md: state.metadata,
  });

  // Convert to UTF-8 bytes, then to base64
  const utf8Bytes = new TextEncoder().encode(json);
  const binaryString = Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join("");
  const encoded = btoa(binaryString);
  return encoded;
}

/**
 * Deserializes state from URL hash string
 * Uses UTF-8 decoding to support Unicode characters (emojis, non-Latin scripts, etc.)
 */
export function deserializeState(hash: string): AppState {
  try {
    // Remove leading # if present
    const cleaned = hash.startsWith("#") ? hash.slice(1) : hash;

    if (!cleaned) {
      return createDefaultState();
    }

    // Decode from base64 to binary string, then to UTF-8
    const binaryString = atob(cleaned);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json);

    const state: AppState = {
      names: parsed.n || [],
      constraints: parsed.c || [],
      seed: parsed.s || Date.now(),
      metadataFields: parsed.mf || [],
      metadata: parsed.md || [],
    };

    validateState(state);
    return state;
  } catch (error) {
    if (error instanceof InvalidStateError) {
      throw error;
    }
    const message = error instanceof Error ? error.message : String(error);
    throw new InvalidStateError(`Failed to deserialize state: ${message}`);
  }
}

/**
 * Adds a new person to the state
 */
export function addPerson(state: AppState, name: string): AppState {
  const n = state.names.length;
  const newNames = [...state.names, name];

  // Expand constraint matrix
  const newConstraints: Constraint[][] = state.constraints.map((row) => [
    ...row,
    Constraint.CAN,
  ]);

  // Add new row for the new person
  const newRow = Array.from({ length: n + 1 }, (_, i) =>
    i === n ? Constraint.CANNOT : Constraint.CAN
  );
  newConstraints.push(newRow);

  // Set diagonal to CANNOT
  for (let i = 0; i < n; i++) {
    newConstraints[i][i] = Constraint.CANNOT;
  }

  // Add empty metadata row for new person
  const newMetadata = [
    ...state.metadata,
    Array.from({ length: state.metadataFields.length }, () => ""),
  ];

  return {
    ...state,
    names: newNames,
    constraints: newConstraints,
    metadata: newMetadata,
  };
}

/**
 * Removes a person from the state
 */
export function removePerson(state: AppState, index: number): AppState {
  if (index < 0 || index >= state.names.length) {
    throw new InvalidStateError(`Invalid person index: ${index}`);
  }

  const newNames = state.names.filter((_, i) => i !== index);
  const newConstraints = state.constraints
    .filter((_, i) => i !== index)
    .map((row) => row.filter((_, j) => j !== index));
  const newMetadata = state.metadata.filter((_, i) => i !== index);

  return {
    ...state,
    names: newNames,
    constraints: newConstraints,
    metadata: newMetadata,
  };
}

/**
 * Updates a constraint in the matrix
 */
export function updateConstraint(
  state: AppState,
  from: number,
  to: number,
  constraint: Constraint,
): AppState {
  const n = state.names.length;

  if (from < 0 || from >= n || to < 0 || to >= n) {
    throw new InvalidStateError(`Invalid indices: from=${from}, to=${to}`);
  }

  if (from === to && constraint !== Constraint.CANNOT) {
    throw new InvalidStateError("Cannot allow a person to give to themselves");
  }

  const newConstraints = state.constraints.map((row) => [...row]);
  newConstraints[from][to] = constraint;

  return {
    ...state,
    constraints: newConstraints,
  };
}

/**
 * Updates the random seed
 */
export function updateSeed(state: AppState, seed: number): AppState {
  return {
    ...state,
    seed,
  };
}

/**
 * Adds a new metadata field
 */
export function addMetadataField(state: AppState, fieldName: string): AppState {
  const newMetadataFields = [...state.metadataFields, fieldName];
  const newMetadata = state.metadata.map((row) => [...row, ""]);

  return {
    ...state,
    metadataFields: newMetadataFields,
    metadata: newMetadata,
  };
}

/**
 * Removes a metadata field
 */
export function removeMetadataField(state: AppState, fieldIndex: number): AppState {
  if (fieldIndex < 0 || fieldIndex >= state.metadataFields.length) {
    throw new InvalidStateError(`Invalid field index: ${fieldIndex}`);
  }

  const newMetadataFields = state.metadataFields.filter((_, i) => i !== fieldIndex);
  const newMetadata = state.metadata.map((row) => row.filter((_, i) => i !== fieldIndex));

  return {
    ...state,
    metadataFields: newMetadataFields,
    metadata: newMetadata,
  };
}

/**
 * Updates a metadata value
 */
export function updateMetadata(
  state: AppState,
  personIndex: number,
  fieldIndex: number,
  value: string,
): AppState {
  if (personIndex < 0 || personIndex >= state.names.length) {
    throw new InvalidStateError(`Invalid person index: ${personIndex}`);
  }

  if (fieldIndex < 0 || fieldIndex >= state.metadataFields.length) {
    throw new InvalidStateError(`Invalid field index: ${fieldIndex}`);
  }

  const newMetadata = state.metadata.map((row, i) =>
    i === personIndex
      ? row.map((val, j) => (j === fieldIndex ? value : val))
      : [...row]
  );

  return {
    ...state,
    metadata: newMetadata,
  };
}
